import { useMemo } from "react";
import { Mail, ShieldCheck, UserCheck, UserX } from "lucide-react";
import { cn } from "@/lib/cn";

type Member = { role: string; status: string; customRoleId: string | null };

type Props = {
  members: Member[];
  pendingInvitesCount: number;
};

/** Resumo compacto no topo de Equipe — chips, não card de dashboard (de propósito: 4 números,
 *  sem gráfico, sem ícone gigante). Deriva tudo dos dados já buscados por quem chama, sem query
 *  própria. */
export function TeamSummaryChips({ members, pendingInvitesCount }: Props) {
  const activeCount = useMemo(() => members.filter((m) => m.status === "ACTIVE").length, [members]);
  const suspendedCount = useMemo(() => members.filter((m) => m.status === "SUSPENDED").length, [members]);
  const rolesInUseCount = useMemo(
    () => new Set(members.map((m) => m.customRoleId ?? m.role)).size,
    [members],
  );

  // Rótulo concorda com o número: "1 papel em uso", "2 papéis em uso". Fixar no plural produz
  // "1 Papéis em uso", que salta aos olhos justamente no caso mais comum de organização pequena.
  const plural = (n: number, singular: string, muitos: string) => (n === 1 ? singular : muitos);

  const chips = [
    { icon: UserCheck, label: plural(activeCount, "Membro ativo", "Membros ativos"), value: activeCount, accent: "#0ea5e9" },
    { icon: Mail, label: plural(pendingInvitesCount, "Convite pendente", "Convites pendentes"), value: pendingInvitesCount, accent: "#f59e0b" },
    { icon: UserX, label: plural(suspendedCount, "Suspenso", "Suspensos"), value: suspendedCount, accent: "#ef4444" },
    { icon: ShieldCheck, label: plural(rolesInUseCount, "Papel em uso", "Papéis em uso"), value: rolesInUseCount, accent: "#2457ff" },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 border-b border-zinc-100 px-6 py-3.5 dark:border-border sm:grid-cols-4">
      {chips.map(({ icon: Icon, label, value, accent }) => (
        <div
          key={label}
          className={cn(
            "flex items-center gap-2 rounded-lg border border-zinc-200 px-2.5 py-2 dark:border-border",
          )}
        >
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: `${accent}1a`, color: accent }}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight text-zinc-900 dark:text-foreground">
              {value}
            </p>
            {/* Quebra em vez de truncar no celular: em 360px cada chip fica com ~150px e
                "Convites pendentes" era cortado — o rótulo é o que dá sentido ao número. */}
            <p className="text-[10.5px] leading-tight text-zinc-500 sm:truncate dark:text-muted-foreground">
              {label}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
